import axios from "axios";
import {
  messagingApiConfig,
  peopleApiConfig,
  contentApiConfig,
  objectToQueryString,
} from "./helpers";
import { cf } from "../../utils";
import { logError } from "../../utils/zharp";

// HTTP failures are logged by the axios interceptor in utils/zharp; this
// covers 2xx responses whose envelope status isn't a success.
const reportEnvelopeError = (method, url, response) => {
  logError(`messaging-api ${method.toUpperCase()} ${url} failed`, {
    source: "http",
    httpStatus: response.status,
    status: response.data ? response.data.status : undefined,
  });
};

const messagingRequest = ({
  method,
  url,
  params,
  body,
  contentType = "application/json",
  shouldResolve = true,
  cancelToken,
  responseType,
}) => {
  return new Promise((resolve, reject) => {
    axios
      .request({
        url,
        baseURL: `${cf.MESSAGING_API_URL}/api/v1`,
        method,
        headers: messagingApiConfig.headers(contentType),
        params: params ? params : undefined,
        data: method !== "get" ? body : undefined,
        paramsSerializer: objectToQueryString,
        ...(cancelToken ? { cancelToken } : {}),
        ...(responseType ? { responseType } : {}),
      })
      .then((response) => {
        if (shouldResolve) {
          const { status, data } = response.data;
          if (status === 200 || status === 201) {
            resolve(data);
          } else {
            reportEnvelopeError(method, url, response);
            reject(response);
          }
        } else {
          resolve(response);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const messagingApi = {
  request: (args) => messagingRequest(args),
  get: (args) => messagingRequest({ method: "get", ...args }),
  post: (args) => messagingRequest({ ...args, method: "post" }),
  delete: (args) => messagingRequest({ ...args, method: "delete" }),
};

const peopleRequest = ({
  method,
  url,
  params,
  body,
  contentType = "application/json",
  shouldResolve = true,
  cancelToken,
}) => {
  // When body is FormData, Axios must set Content-Type automatically so it can
  // include the correct multipart boundary. Passing any explicit content-type
  // string (even "multipart/form-data") without the boundary will break the upload.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const resolvedContentType = isFormData ? undefined : contentType;

  return new Promise((resolve, reject) => {
    axios
      .request({
        url,
        baseURL: cf.PEOPLE_API_URL,
        method,
        headers: peopleApiConfig.headers(resolvedContentType),
        params: params ? params : undefined,
        data: method !== "get" ? body : undefined,
        paramsSerializer: objectToQueryString,
        ...(cancelToken ? { cancelToken } : {}),
      })
      .then((response) => {
        if (shouldResolve) {
          resolve(response.data);
        } else {
          resolve(response);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const peopleApi = {
  request: (args) => peopleRequest(args),
  get: (args) => peopleRequest({ method: "get", ...args }),
  post: (args) => peopleRequest({ ...args, method: "post" }),
  delete: (args) => peopleRequest({ ...args, method: "delete" }),
};

const contentRequest = ({
  method,
  url,
  params,
  body,
  contentType = "application/json",
  shouldResolve = true,
  cancelToken,
}) => {
  return new Promise((resolve, reject) => {
    axios
      .request({
        url,
        baseURL: cf.CONTENT_API_URL,
        method,
        headers: contentApiConfig.headers(contentType),
        params: params ? params : undefined,
        data: method !== "get" ? body : undefined,
        paramsSerializer: objectToQueryString,
        ...(cancelToken ? { cancelToken } : {}),
      })
      .then((response) => {
        if (shouldResolve) {
          resolve(response.data && response.data.data ? response.data.data : []);
        } else {
          resolve(response);
        }
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const contentApi = {
  request: (args) => contentRequest(args),
  get: (args) => contentRequest({ method: "get", ...args }),
  post: (args) => contentRequest({ ...args, method: "post" }),
  delete: (args) => contentRequest({ ...args, method: "delete" }),
};
